import { useState, useEffect } from 'react';
import {
  IconShoppingCartFilled,
  IconCalendarWeekFilled,
  IconPigFilled,
  IconClipboardCheckFilled,
  IconMugFilled,
  IconCategoryFilled,
  IconBoxMultipleFilled,
  IconReceiptFilled,
  IconSettingsFilled,
  IconGlassFilled,
  IconBugFilled,
  IconPrinter,
  IconChefHatFilled,
  IconDeviceMobileFilled,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react';
import SidebarButton from './SidebarButton';
import Alerta from './Alerta';
import TutorialButton from '../Utils/TutorialButton';
import { canAccess } from '../api/client';
import { getSavedPrinter, connectPrinter, isBluetoothSupported } from '../Utils/printer';

const DIVIDER = { backgroundColor: 'rgba(255, 255, 255, 0.16)' };

// Los módulos se agrupan por área para que el riel tenga ritmo y se lea de un vistazo.
const NAV_GROUPS = [
  [
    { id: 'inicio', icon: IconShoppingCartFilled, label: 'Punto de venta' },
    { id: 'comandero', icon: IconDeviceMobileFilled, label: 'Comandero' },
    { id: 'ordenes', icon: IconClipboardCheckFilled, label: 'Pedidos' },
  ],
  [
    { id: 'productos', icon: IconMugFilled, label: 'Productos' },
    { id: 'categorias', icon: IconCategoryFilled, label: 'Categorías' },
    { id: 'inventario', icon: IconBoxMultipleFilled, label: 'Inventario' },
  ],
  [
    { id: 'finanzas', icon: IconPigFilled, label: 'Finanzas' },
    { id: 'gastos', icon: IconReceiptFilled, label: 'Gastos' },
  ],
  [
    { id: 'barra', icon: IconGlassFilled, label: 'Barra' },
    { id: 'cocina', icon: IconChefHatFilled, label: 'Cocina' },
  ],
  [
    { id: 'calendario', icon: IconCalendarWeekFilled, label: 'Calendario' },
    { id: 'configuracion', icon: IconSettingsFilled, label: 'Configuración' },
  ],
];

export default function Barra({ active, onNavigate, collapsed, onToggle, onTutorial }) {
  // No todos los usuarios pueden ver lo mismo: el Manager manda un slug de vista
  // por módulo y aquí solo se pintan los que le autorizaron a este usuario.
  const visibleGroups = NAV_GROUPS.map((group) =>
    group.filter((btn) => canAccess(btn.id))
  ).filter((group) => group.length > 0);

  const [openGroups, setOpenGroups] = useState(() => {
    const initial = new Set();
    visibleGroups.forEach((group, index) => {
      if (index === 0 || group.some((btn) => btn.id === active)) initial.add(index);
    });
    return initial;
  });

  useEffect(() => {
    visibleGroups.forEach((group, index) => {
      if (group.some((btn) => btn.id === active)) {
        setOpenGroups((prev) => {
          if (prev.has(index)) return prev;
          const next = new Set(prev);
          next.add(index);
          return next;
        });
      }
    });
  }, [active]);

  const toggleGroup = (index) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  return (
    <div
      className={`relative flex h-screen shrink-0 ${
        collapsed ? 'w-9 items-center justify-center' : ''
      }`}
    >
      {!collapsed && (
        <aside className="flex flex-col items-center h-screen w-[88px] bg-black border-r border-white/[0.16]">
          <div className="flex justify-center shrink-0 pt-5 pb-4">
            <Alerta onNavigate={onNavigate} />
          </div>

          <div className="w-10 h-px shrink-0" style={DIVIDER} />

          <nav
            className="flex flex-col items-center gap-1.5 flex-1 min-h-0 w-full overflow-y-auto pedidos-scroll pt-3 pb-3"
            aria-label="Navegación principal"
          >
            {visibleGroups.map((group, index) => (
              <div key={index} className="flex flex-col items-center gap-1.5 w-full">
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(index)}
                    aria-expanded={openGroups.has(index)}
                    aria-label={openGroups.has(index) ? 'Ocultar sección' : 'Mostrar sección'}
                    className="flex items-center justify-center w-8 h-8 shrink-0 my-1 rounded-full border-none cursor-pointer transition-colors duration-200 ease-out active:scale-[0.96] hover:bg-white/[0.14]"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(20px)',
                      WebkitBackdropFilter: 'blur(20px)',
                    }}
                  >
                    {openGroups.has(index) ? (
                      <IconChevronUp size={14} color="rgba(255, 255, 255, 0.75)" stroke={2.4} />
                    ) : (
                      <IconChevronDown size={14} color="rgba(255, 255, 255, 0.75)" stroke={2.4} />
                    )}
                  </button>
                )}
                {openGroups.has(index) &&
                  group.map((btn) => (
                    <SidebarButton
                      key={btn.id}
                      icon={btn.icon}
                      label={btn.label}
                      active={active === btn.id}
                      onClick={() => onNavigate(btn.id)}
                    />
                  ))}
              </div>
            ))}
          </nav>

          <div className="w-10 h-px shrink-0" style={DIVIDER} />

          <div className="shrink-0 pt-3 pb-5 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => onNavigate('reporte')}
              title="Reportar problema"
              className="flex items-center justify-center w-9 h-9 rounded-full border-none cursor-pointer transition-colors duration-200 hover:bg-white/[0.14]"
              style={{ backgroundColor: 'rgba(255, 69, 58, 0.12)' }}
            >
              <IconBugFilled size={16} color="#ff453a" />
            </button>
            <button
              type="button"
              onClick={async () => {
                try { await connectPrinter(); } catch (e) { console.error(e); }
              }}
              title={getSavedPrinter() ? (getSavedPrinter().name || 'Impresora conectada') : 'Conectar impresora'}
              className="flex items-center justify-center w-9 h-9 rounded-full border-none cursor-pointer transition-colors duration-200 hover:bg-white/[0.14]"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
            >
              <IconPrinter size={16} color={getSavedPrinter() ? '#30d158' : 'rgba(255,255,255,0.5)'} />
            </button>
            <TutorialButton onClick={onTutorial} iconOnly />
          </div>
        </aside>
      )}

      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? 'Mostrar barra de navegación' : 'Ocultar barra de navegación'}
        title={collapsed ? 'Mostrar barra' : 'Ocultar barra'}
        className="flex items-center justify-center w-9 h-14 rounded-full border-none cursor-pointer bg-white/[0.07] backdrop-blur-xl transition-colors duration-200 ease-out hover:bg-white/[0.12] active:scale-[0.96]"
        style={
          collapsed
            ? { boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)' }
            : {
                position: 'absolute',
                zIndex: 20,
                right: '-18px',
                top: '50%',
                transform: 'translateY(-50%)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
              }
        }
      >
        {collapsed ? (
          <IconChevronRight size={15} color="rgba(255, 255, 255, 0.7)" stroke={2.2} />
        ) : (
          <IconChevronLeft size={15} color="rgba(255, 255, 255, 0.7)" stroke={2.2} />
        )}
      </button>
    </div>
  );
}
